import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuyspread.space';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'VigorBuy Spreadsheet In-Depth Review: Features, Benefits & Real-World Performance',
  description: 'An honest, comprehensive review of the VigorBuy Spreadsheet platform — examining template quality, ease of use, security, value, and real business impact based on extensive testing and user feedback.',
  author: {
    '@type': 'Organization',
    name: 'VigorBuy Spread Team',
  },
  publisher: {
    '@type': 'Organization',
    name: 'VigorBuy Spread',
    url: siteUrl,
  },
  datePublished: '2026-07-22',
  dateModified: '2026-07-22',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/vigorbuy-spreadsheet-review`,
  },
  inLanguage: 'en',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'In-Depth Review',
      item: `${siteUrl}/vigorbuy-spreadsheet-review`,
    },
  ],
};

const reviewHighlights = [
  { icon: 'ri-star-fill', title: 'Template Quality', score: '9.4/10', desc: 'Industry-leading curated templates that require minimal customization. Every template includes pre-built formulas, conditional formatting, and data validation rules.' },
  { icon: 'ri-rocket-2-line', title: 'Ease of Use', score: '9.2/10', desc: 'Template-driven approach means you start with a working solution, not a blank canvas. The guided setup process takes minutes rather than hours.' },
  { icon: 'ri-shield-check-line', title: 'Security', score: '8.8/10', desc: 'Comprehensive security practices embedded in every template including access control recommendations, backup strategies, and safe sharing protocols.' },
  { icon: 'ri-price-tag-3-line', title: 'Value for Money', score: '9.5/10', desc: 'Exceptional ROI compared to building spreadsheet solutions from scratch. The time savings alone typically recoup the investment within the first week of use.' },
  { icon: 'ri-customer-service-2-line', title: 'Support Resources', score: '8.6/10', desc: 'Detailed documentation, step-by-step guides, and FAQ resources. The knowledge base covers common scenarios and edge cases thoroughly.' },
  { icon: 'ri-refresh-line', title: 'Update Frequency', score: '9.0/10', desc: 'Templates and guides are refreshed quarterly to incorporate new platform features, user feedback, and evolving business practices.' },
];

export default function ReviewsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <article className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <nav className="mb-8">
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors flex items-center gap-1.5">
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Back to Home
            </Link>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-semibold">Review</span>
              <span className="px-3 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-semibold">2026 Edition</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-900 leading-tight mb-4">
              VigorBuy Spreadsheet In-Depth Review: Features, Benefits &amp; Real-World Performance
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
              An honest, comprehensive review of the VigorBuy Spreadsheet platform — examining template quality, ease of use, security, value, and real business impact based on extensive testing and user feedback.
            </p>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20clean%20workspace%20with%20review%20scorecards%20and%20data%20analytics%20dashboard%20elements%20on%20soft%20warm%20cream%20background%2C%20gentle%20coral%20and%20sage%20accent%20colors%2C%20minimalist%20aesthetic%20with%20organized%20layout%2C%20natural%20diffused%20lighting%2C%20editorial%20product%20photography%20style%2C%20abstract%20geometric%20data%20visualization%20shapes%2C%20professional%20review%20atmosphere%2C%20no%20text%2C%20no%20faces%2C%20no%20screens%20with%20visible%20content&width=1200&height=600&seq=reviews-hero-2026-v2&orientation=landscape"
              alt="VigorBuy Spreadsheet review visualization"
              className="w-full h-auto object-cover object-center"
            />
          </div>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Our Assessment Methodology</h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              This review evaluates <strong>VigorBuy Spreadsheet</strong> across six critical dimensions: template quality, ease of use, security, value for money, support resources, and update frequency. Each dimension is scored on a 10-point scale based on hands-on testing of the complete template library, documentation review, and comparison against alternative spreadsheet solutions.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              We tested templates across industries — ecommerce, finance, marketing, and project management — using real business scenarios with actual data volumes. Our goal is to help you determine whether VigorBuy Spreadsheet is the right resource investment for your specific needs. For a broader context, see our <Link to="/vigorbuy-spreadsheet-comparison" className="text-primary-500 hover:text-primary-600 font-medium underline">platform comparison guide</Link>.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2 text-center">Overall Score</h2>
            <div className="text-center mb-6">
              <span className="font-heading text-6xl font-extrabold text-primary-500">9.1</span>
              <span className="text-foreground-500 text-lg ml-1">/ 10</span>
            </div>
            <p className="text-foreground-600 text-sm text-center max-w-md mx-auto">Based on comprehensive testing across all six evaluation dimensions with real-world business scenarios.</p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">Detailed Category Scores</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {reviewHighlights.map((item) => (
                <div key={item.title} className="p-5 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center">
                        <i className={`${item.icon} text-accent-500 w-4 h-4 flex items-center justify-center`}></i>
                      </div>
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm">{item.title}</h3>
                    </div>
                    <span className="text-lg font-extrabold text-primary-500">{item.score}</span>
                  </div>
                  <p className="text-foreground-600 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">What Users Are Saying</h2>
            <div className="flex flex-col gap-4">
              {[
                { name: 'Ecommerce Store Owner', quote: 'Switching to VigorBuy Spreadsheet templates cut my daily operations time from 3 hours to under 45 minutes. The inventory tracker alone saved me from at least two stockout situations this quarter.' },
                { name: 'Marketing Agency Director', quote: 'We consolidated five different client reporting spreadsheets into one unified template. Our team now delivers weekly reports in 20 minutes instead of two hours.' },
                { name: 'Finance Manager', quote: 'The cash flow planner is genuinely the best pre-built financial template I have used. The automated variance analysis caught a budgeting error that would have cost us thousands.' },
                { name: 'Small Business Owner', quote: 'I was skeptical about using pre-made templates, but the customization zones make it easy to adapt without breaking the core formulas. It feels like it was built for my exact workflow.' },
              ].map((review, i) => (
                <div key={i} className="p-5 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed mb-3 italic">&ldquo;{review.quote}&rdquo;</p>
                  <p className="text-foreground-900 text-xs font-semibold">— {review.name}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Strengths &amp; Areas for Improvement</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="p-5 rounded-2xl bg-primary-50 border border-primary-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-thumb-up-line text-primary-600 w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm">What We Loved</h3>
                </div>
                <ul className="flex flex-col gap-2">
                  {['Ready-to-use templates eliminate setup time', 'Clear, well-documented customization zones', 'Cross-platform compatibility', 'Quarterly updates keep content fresh', 'Strong security guidance embedded in every template', 'Excellent value relative to time saved'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-foreground-700 text-xs">
                      <i className="ri-check-line text-primary-500 w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-5 rounded-2xl bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
                    <i className="ri-lightbulb-line text-secondary-600 w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm">Room to Grow</h3>
                </div>
                <ul className="flex flex-col gap-2">
                  {['More industry-specific vertical templates', 'Video walkthroughs for complex templates', 'Community forum for peer support', 'API integration templates for advanced users'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-foreground-700 text-xs">
                      <i className="ri-arrow-right-line text-secondary-500 w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Final Verdict: Is It Worth It?</h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              VigorBuy Spreadsheet earns a strong recommendation for business professionals who value their time and want structured, reliable spreadsheet solutions without the overhead of building from scratch. The template quality consistently exceeds expectations, and the guided approach makes even complex workflows accessible to users with moderate spreadsheet experience.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              The platform is not a replacement for advanced Excel modeling or real-time Google Sheets collaboration — and it does not claim to be. Instead, it serves as the essential middle layer that transforms raw spreadsheet software into business-ready tools. For ecommerce sellers, small business owners, marketing professionals, and operations managers, the time savings alone justify the investment many times over.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              For a deeper dive into specific features, read our <Link to="/vigorbuy-spreadsheet-templates" className="text-primary-500 hover:text-primary-600 font-medium underline">complete templates guide</Link> and <Link to="/vigorbuy-spreadsheet-security" className="text-primary-500 hover:text-primary-600 font-medium underline">security analysis</Link>. If you are comparing platforms, the <Link to="/vigorbuy-spreadsheet-comparison" className="text-primary-500 hover:text-primary-600 font-medium underline">detailed comparison guide</Link> provides additional context.
            </p>
          </section>

          <section className="mb-10">
            <div className="flex flex-col sm:flex-row items-center gap-4 p-6 rounded-2xl bg-primary-50 border border-primary-200">
              <div className="flex-1">
                <h3 className="font-heading font-bold text-foreground-900 text-lg mb-1">Ready to Try It Yourself?</h3>
                <p className="text-foreground-600 text-sm">Experience the template library firsthand and see the difference in your workflow.</p>
              </div>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-bold hover:bg-primary-600 transition-all duration-200 shadow-lg shadow-primary-500/20 whitespace-nowrap text-center cursor-pointer"
              >
                Visit Main Store
              </a>
            </div>
          </section>

          <div className="border-t border-background-200/70 pt-8 mt-8">
            <p className="text-foreground-500 text-xs text-center mb-4">
              This review is part of the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">VigorBuy Spread</Link> knowledge base. Explore more: <Link to="/vigorbuy-spreadsheet-complete-guide" className="text-primary-500 hover:text-primary-600 font-medium">complete guide</Link> &middot; <Link to="/vigorbuy-spreadsheet-templates" className="text-primary-500 hover:text-primary-600 font-medium">templates</Link> &middot; <Link to="/vigorbuy-spreadsheet-security" className="text-primary-500 hover:text-primary-600 font-medium">security</Link>.
            </p>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}